import {
  FOCOS, HORIZONTES, OBJECTIVE_CRITERIA, normGoal,
  type Insight, type MaterialDto, type ObjectiveAnalysisDto, type Objetivo,
} from '@ads/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { SelectField } from '../components/Field';
import * as Ic from '../components/icons';
import { keys, useAnalyzeObjective, useMaterialAction, useMaterials, useObjective, useSession, useUpload } from '../lib/queries';

/* ─────────────────────────────────────────────────────────────────────
   Início · o que o ADS devolve enquanto a organização se apresenta:
   a leitura do objetivo (interpretação, horizonte, focos e força) e os
   materiais compreendidos (lidos, catalogados, consultáveis por todos
   os agentes).
   ───────────────────────────────────────────────────────────────────── */

const MIN_GOAL = 20;
const IDLE_MS = 2200;
type Field = 'horizon' | 'focus' | 'focus2';
const FIELDS: Field[] = ['horizon', 'focus', 'focus2'];

export type ReadingState = 'empty' | 'short' | 'waiting' | 'analyzing' | 'error' | 'ready';

/**
 * A frase do objetivo, lida pelo ADS: pede a interpretação quando a pessoa
 * para de escrever (ou sai do campo) e preenche horizonte e focos — sem
 * passar por cima do que ela já ajustou à mão.
 */
export function useObjectiveReading(goal: string, objetivo: Objetivo | null, apply: (patch: Partial<Objetivo>) => void) {
  const { projectId } = useSession();
  const qc = useQueryClient();
  const obj = useObjective();
  const analyze = useAnalyzeObjective();
  const latest = useRef<string | null>(null);
  const applied = useRef<string | null>(null);
  const prev = useRef<ObjectiveAnalysisDto | null>(null);

  const run = useCallback((text: string, force = false) => {
    const g = text.trim();
    if (g.length < MIN_GOAL) return;
    const n = normGoal(g);
    const cur = qc.getQueryData<ObjectiveAnalysisDto | null>(keys.objective(projectId));
    if (!force && cur && !cur.note && normGoal(cur.goal) === n) return;
    if (!force && latest.current === n && analyze.isPending) return;
    latest.current = n;
    analyze.mutate({ goal: g, force }, {
      onSuccess: dto => { if (latest.current === normGoal(dto.goal)) qc.setQueryData(keys.objective(projectId), dto); },
    });
  }, [analyze, qc, projectId]);

  /* parou de escrever: lê */
  useEffect(() => {
    if (!obj.isFetched) return;
    const t = setTimeout(() => run(goal), IDLE_MS);
    return () => clearTimeout(t);
  }, [goal, obj.isFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  /* chegou uma leitura nova: sugere o que está vazio ou ainda igual à sugestão anterior */
  useEffect(() => {
    const d = obj.data;
    if (!d || !objetivo || applied.current === d.updatedAt) return;
    const before = prev.current;
    applied.current = d.updatedAt;
    prev.current = d;
    const patch: Partial<Record<Field, string>> = {};
    for (const k of FIELDS) {
      const cur = objetivo[k];
      if (!cur || (before && cur === before.analysis[k])) patch[k] = d.analysis[k];
    }
    const focus = patch.focus ?? objetivo.focus;
    const focus2 = patch.focus2 ?? objetivo.focus2;
    if (focus && focus === focus2) patch.focus2 = d.analysis.focus !== focus ? d.analysis.focus : d.analysis.focus2 !== focus ? d.analysis.focus2 : '';
    if (Object.keys(patch).length) apply(patch as Partial<Objetivo>);
  }, [obj.data, objetivo, apply]);

  const g = goal.trim();
  const fresh = !!obj.data && normGoal(obj.data.goal) === normGoal(g);
  const state: ReadingState = !g ? 'empty'
    : analyze.isPending ? 'analyzing'
    : g.length < MIN_GOAL ? 'short'
    : fresh ? 'ready'
    : analyze.isError ? 'error'
    : 'waiting';
  return {
    state, dto: obj.data ?? null, fresh,
    error: analyze.error instanceof Error ? analyze.error.message : null,
    now: () => run(goal),
    again: () => run(goal, true),
  };
}

/* ── painel do objetivo ───────────────────────────────────────────── */

const strengthWord = (n: number) => (n >= 85 ? 'Forte' : n >= 70 ? 'Consistente' : n >= 55 ? 'Em construção' : 'Frágil');

export function ObjectivePanel({ reading, objetivo, onField }: {
  reading: ReturnType<typeof useObjectiveReading>;
  objetivo: Objetivo;
  onField: (patch: Partial<Objetivo>) => void;
}) {
  const { state, dto, fresh } = reading;
  if (state === 'empty' || (state === 'short' && !dto)) {
    return (
      <div className="obj-panel is-empty">
        <span className="obj-ic"><Ic.Sparkle /></span>
        <p>{state === 'short'
          ? 'Escreva um pouco mais — uma ou duas frases bastam para o ADS interpretar.'
          : 'Ao escrever o objetivo, o ADS interpreta a intenção, sugere horizonte e focos e mede a força do objetivo.'}</p>
      </div>
    );
  }
  if (!dto) {
    return (
      <div className="obj-panel is-loading" aria-busy="true">
        <div className="obj-head"><h4><Ic.Target />Objetivo interpretado</h4><Status state={state} error={reading.error} onRetry={reading.now} /></div>
        <div className="obj-skel"><i /><i /><i /></div>
      </div>
    );
  }

  const a = dto.analysis;
  const setField = (k: Field, v: string) => {
    const patch: Partial<Objetivo> = { [k]: v } as Partial<Objetivo>;
    /* foco principal igual ao secundário: troca os dois de lugar */
    if (k === 'focus' && v && v === objetivo.focus2) patch.focus2 = objetivo.focus;
    onField(patch);
  };
  const tag = (k: Field) => {
    const v = objetivo[k];
    if (!v) return null;
    if (v === a[k]) return <span className="obj-tag"><Ic.Sparkle />Sugestão do ADS</span>;
    return (
      <span className="obj-tag edited">Ajustado por você
        {!(k === 'focus2' && a.focus2 === objetivo.focus) && (
          <button type="button" onClick={() => setField(k, a[k])}>usar “{a[k]}”</button>
        )}
      </span>
    );
  };

  return (
    <div className={`obj-panel${fresh ? '' : ' is-stale'}`} aria-live="polite">
      <div className="obj-head">
        <h4><Ic.Target />Objetivo interpretado</h4>
        <Status state={state} error={reading.error} onRetry={reading.now} onAgain={reading.again} basic={dto.basic} />
      </div>
      <p className="obj-int">{a.interpreted}</p>

      <div className="grid c3 obj-fields">
        <div className="f-wrap">
          <SelectField id="obj-h" label={<><Ic.Clock />Horizonte sugerido</>} value={objetivo.horizon} options={HORIZONTES} onChange={v => setField('horizon', v)} />
          {tag('horizon')}
        </div>
        <div className="f-wrap">
          <SelectField id="obj-f1" label={<><Ic.Target />Foco principal</>} value={objetivo.focus} options={FOCOS} onChange={v => setField('focus', v)} />
          {tag('focus')}
        </div>
        <div className="f-wrap">
          <SelectField id="obj-f2" label={<><Ic.Bars />Foco secundário</>} value={objetivo.focus2} options={FOCOS.filter(f => f !== objetivo.focus)} onChange={v => setField('focus2', v)} />
          {tag('focus2')}
        </div>
      </div>

      <div className="obj-strength">
        <div className="obj-score">
          <span className="k">Força do objetivo</span>
          <span className="v"><b>{dto.strength}</b><small>/100</small></span>
          <span className="w">{strengthWord(dto.strength)}</span>
        </div>
        <div className="bars stacked obj-crit">
          {OBJECTIVE_CRITERIA.map(c => (
            <div className="bar" key={c.key}>
              <span className="l">{c.label}</span>
              <span className="t"><i style={{ '--v': `${a.scores[c.key]}%` } as CSSProperties} /></span>
              <span className="n">{a.scores[c.key]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="obj-sub">Leituras do ADS</div>
      <Insights items={a.insights} />

      <p className="obj-foot"><Ic.Help />Este objetivo orienta a linguagem, os benchmarks e as recomendações das próximas etapas.
        {dto.note && <em> {dto.note}</em>}</p>
    </div>
  );
}

function Status({ state, error, onRetry, onAgain, basic }: { state: ReadingState; error: string | null; onRetry: () => void; onAgain?: () => void; basic?: boolean }) {
  if (state === 'analyzing') return <span className="obj-st busy"><span className="spin" />Interpretando…</span>;
  if (state === 'waiting') return <span className="obj-st">A leitura atualiza quando você parar de escrever</span>;
  if (state === 'short') return <span className="obj-st">Escreva um pouco mais para atualizar</span>;
  if (state === 'error') return <span className="obj-st bad"><Ic.Warning />{error ?? 'Não foi possível interpretar agora.'}<button type="button" onClick={onRetry}>Tentar de novo</button></span>;
  return (
    <span className="obj-st">
      <span title={basic ? 'Sem IA: modo demonstração, ou o modelo não respondeu. Com o Claude ligado, a leitura é mais fina.' : undefined}>{basic ? 'Leitura básica' : 'Atualizado'}</span>
      {onAgain && <button type="button" onClick={onAgain} aria-label="Interpretar de novo"><Ic.Refresh />Interpretar de novo</button>}
    </span>
  );
}

/* ── leituras: cartões curtos ─────────────────────────────────────── */

const INSIGHT_ICON: Record<Insight['kind'], ReactNode> = {
  forca: <Ic.Check />, contexto: <Ic.Layers />, lacuna: <Ic.Bars />, proximo: <Ic.ArrowRight />,
};

export function Insights({ items }: { items: Insight[] }) {
  return (
    <ul className="insights">
      {items.map((i, n) => (
        <li key={n} data-kind={i.kind} style={{ '--i': n } as CSSProperties}>
          <span className="ins-ic">{INSIGHT_ICON[i.kind]}</span><span>{i.text}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── materiais ────────────────────────────────────────────────────── */

const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);
const extOf = (name: string) => (/\.([a-z0-9]{1,5})$/i.exec(name)?.[1] ?? '').toUpperCase();
const SINGULAR: Record<string, string> = { 'páginas': 'página', slides: 'slide', planilhas: 'planilha', arquivos: 'arquivo' };
function measure(m: MaterialDto): string {
  const c = m.catalog;
  const parts: string[] = [];
  if (c?.pages && c.unit) parts.push(`${c.pages} ${c.pages === 1 ? SINGULAR[c.unit] ?? c.unit : c.unit}`);
  if (c?.words) parts.push(`${c.words.toLocaleString('pt-BR')} palavras`);
  parts.push(size(m.size));
  return parts.join(' · ');
}

type Details = React.DetailsHTMLAttributes<HTMLDetailsElement> & { onToggle: (e: React.SyntheticEvent<HTMLDetailsElement>) => void };

export function Materiais({ details }: { details: Details }) {
  const mats = useMaterials();
  const upload = useUpload();
  const act = useMaterialAction();
  const [busy, setBusy] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const docInput = useRef<HTMLInputElement>(null);
  const logoInput = useRef<HTMLInputElement>(null);

  const send = async (files: FileList | File[], kind: 'document' | 'logo') => {
    setErr(null);
    for (const f of Array.from(files)) {
      setBusy(b => [...b, f.name]);
      try { await upload.mutateAsync({ file: f, kind }); }
      catch (e) { setErr(`${f.name}: ${e instanceof Error ? e.message : 'falha no envio.'}`); }
      finally { setBusy(b => b.filter(x => x !== f.name)); }
    }
  };
  const drop = (kind: 'document' | 'logo') => ({
    onDragOver: (e: DragEvent) => { e.preventDefault(); },
    onDrop: (e: DragEvent) => { e.preventDefault(); if (e.dataTransfer.files.length) void send(e.dataTransfer.files, kind); },
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (kind === 'logo' ? logoInput : docInput).current?.click(); } },
    onClick: () => (kind === 'logo' ? logoInput : docInput).current?.click(),
  });
  const items = mats.data?.items ?? [];
  const reading = items.filter(i => i.reading === 'pending' || i.reading === 'reading').length;
  const read = items.filter(i => i.reading === 'read').length;

  return (
    <details {...details}>
      <summary>
        <span className="no mono">05</span><span className="t">Documentos e materiais</span>
        <span className="st mono">
          <span className={read && !reading ? 'cnt-b done' : undefined}>
            {items.length ? (reading ? `lendo ${reading} de ${items.length}…` : `${read} de ${items.length} lido${items.length > 1 ? 's' : ''}`) : 'Opcional'}
          </span>
          <span className="tog" aria-hidden="true"><i /><i /></span>
        </span>
      </summary>
      <div className="body">
        <div className="grid c2">
          <div className="drop" tabIndex={0} role="button" aria-label="Enviar documentos" {...drop('document')}>
            <span className="ico"><Ic.Upload /></span>
            <div className="t">Arraste seus arquivos aqui</div><div className="s">ou clique para selecionar</div>
            <div className="fmt mono">PDF · Word · PowerPoint · Excel · texto · ZIP — até 50 MB</div>
          </div>
          <div className="drop" tabIndex={0} role="button" aria-label="Enviar logotipo" {...drop('logo')}>
            <span className="ico"><Ic.Image /></span>
            <div className="t">Logotipo da organização</div><div className="s">PNG com fundo transparente</div>
            <div className="fmt mono">Lê identidade e aplicação de marca</div>
          </div>
        </div>
        <input ref={docInput} type="file" hidden multiple
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.zip,.png,.jpg,.jpeg,.webp"
          onChange={e => { if (e.target.files) void send(e.target.files, 'document'); e.target.value = ''; }} />
        <input ref={logoInput} type="file" hidden accept=".png,.svg,.jpg,.jpeg,.webp"
          onChange={e => { if (e.target.files) void send(e.target.files, 'logo'); e.target.value = ''; }} />

        {(items.length > 0 || busy.length > 0 || err) && (
          <>
            <div className="mat-h">
              <h4>Arquivos compreendidos</h4>
              <span>O ADS lê cada arquivo, cataloga o que encontra e guarda para consulta de todos os agentes — do Strategy ao Studio.</span>
            </div>
            <ul className="mats">
              {items.map(m => <MaterialCard key={m.id} m={m} onAction={action => act.mutate({ id: m.id, action })} />)}
              {busy.map(n => (
                <li key={'b' + n} className="mitem pending">
                  <span className="mat-ic"><b>{extOf(n) || '—'}</b></span>
                  <div className="mat-main"><div className="mat-top"><span className="mat-name">{n}</span></div>
                    <div className="mat-meta"><span className="mat-st busy"><span className="spin" />Enviando…</span></div></div>
                </li>
              ))}
            </ul>
            {err && <p className="cfg-status bad mat-err"><Ic.Warning />{err}</p>}
          </>
        )}

        {mats.data?.insights && (
          <>
            <div className="mat-h"><h4>Leituras dos materiais</h4><span>O que os documentos, juntos, dizem sobre a organização.</span></div>
            <Insights items={mats.data.insights} />
          </>
        )}
      </div>
    </details>
  );
}

export function MaterialCard({ m, onAction, onView, viewing }: {
  m: MaterialDto; onAction: (a: 'delete' | 'read') => void;
  /** evidências: abre a visualização do que o ADS leu */
  onView?: () => void; viewing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!confirm) return;
    const t = setTimeout(() => setConfirm(false), 3500);
    return () => clearTimeout(t);
  }, [confirm]);
  const c = m.catalog;
  const st = m.reading;
  const status = st === 'pending' ? <span className="mat-st busy"><span className="spin" />Na fila de leitura</span>
    : st === 'reading' ? <span className="mat-st busy"><span className="spin" />Lendo e catalogando…</span>
    : st === 'read' ? <span className="mat-st ok"><Ic.Check />{
        m.contentType.startsWith('image/') ? (c?.basic ? 'Guardado' : 'Analisado') : c?.basic ? 'Lido · leitura básica' : 'Lido'}</span>
    : st === 'failed' ? <span className="mat-st bad"><Ic.Warning />Não foi possível ler</span>
    : <span className="mat-st"><Ic.Warning />Guardado, não lido</span>;
  const facts = c?.facts ?? [];

  return (
    <li className={`mitem ${st}`}>
      <span className="mat-ic">{m.contentType.startsWith('image/') ? <Ic.Image /> : <b>{extOf(m.filename) || 'DOC'}</b>}</span>
      <div className="mat-main">
        <div className="mat-top">
          <a className="mat-name" href={`/api/uploads/${m.id}/content`} title={`Baixar ${m.filename}`}>{m.filename}</a>
          {confirm
            ? <button type="button" className="mat-del" onClick={() => onAction('delete')}>Excluir</button>
            : <button type="button" className="mat-x" aria-label={`Excluir ${m.filename}`} onClick={() => setConfirm(true)}><Ic.Close /></button>}
        </div>
        <div className="mat-meta">
          {status}
          {c && st !== 'failed' && <span className="mat-tag">{c.category}</span>}
          <span className="mat-size">{measure(m)}</span>
        </div>
        {c && st === 'read' && <p className="mat-sum">{c.summary}</p>}
        {m.readNote && (
          <p className="mat-note">{m.readNote}
            {(st === 'failed' || (st === 'read' && c?.basic)) && <button type="button" onClick={() => onAction('read')}>Ler de novo</button>}
          </p>
        )}
        {onView && st !== 'pending' && st !== 'reading' && (
          <button type="button" className={`mat-view${viewing ? ' on' : ''}`} aria-pressed={viewing} onClick={onView}><Ic.Eye />{viewing ? 'Visualizando' : 'Visualizar'}</button>
        )}
        {st === 'read' && facts.length > 0 && (
          <>
            <button type="button" className="mat-more" aria-expanded={open} onClick={() => setOpen(o => !o)}>
              {open ? 'Esconder' : `${facts.length} ${facts.length === 1 ? 'fato encontrado' : 'fatos encontrados'}`}<Ic.ChevronDown />
            </button>
            {open && (
              <div className="mat-facts">
                {c!.title !== m.filename && <b>{c!.title}</b>}
                <ul>{facts.map((f, i) => <li key={i}>{f}</li>)}</ul>
                {c!.topics.length > 0 && <div className="mat-topics">{c!.topics.map(t => <span key={t}>{t}</span>)}</div>}
                {c!.partial && <em>Documento longo: o catálogo leu o começo; a busca dos agentes cobre o texto inteiro.</em>}
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}
